import { Button } from '@/app/common/components/button/Button'
import Label from '@/common/components/Label.tsx'
import { ProviderAffiliationLogoInput } from '@/queries/providers/affiliations/schema.ts'
import { useRef, useState, useEffect } from 'react'
import Image from '@/common/components/Image.tsx'
import FormErrorMessage from '@/app/common/components/FormErrorMessage.tsx'

const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const

type Props = {
  value?: ProviderAffiliationLogoInput
  onChange: (value: ProviderAffiliationLogoInput) => void
  disabled?: boolean
}

export default function AffiliationLogoField({
  value,
  onChange,
  disabled = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const currentPreview =
    previewSrc ?? (typeof value === 'string' ? value : null)

  useEffect(() => {
    let isCurrent = true
    if (value instanceof File) {
      const reader = new FileReader()
      reader.onload = () => {
        if (!isCurrent) {
          return
        }
        setPreviewSrc(reader.result?.toString() ?? null)
      }
      reader.onerror = () => {
        if (!isCurrent) {
          return
        }
        setError('Unable to read the selected file. Please try again.')
      }
      reader.readAsDataURL(value)
      return () => {
        isCurrent = false
        if (reader.readyState === FileReader.LOADING) {
          reader.abort()
        }
      }
    }
    setPreviewSrc(null)
    if (value == null && inputRef.current) {
      inputRef.current.value = ''
    }
    return () => {
      isCurrent = false
    }
  }, [value])

  const openFilePicker = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current.click()
    }
  }

  const handleFileSelected = (file?: File) => {
    if (!file) {
      return
    }
    if (
      !ACCEPTED_IMAGE_TYPES.includes(
        file.type as (typeof ACCEPTED_IMAGE_TYPES)[number]
      )
    ) {
      setError('Supported formats are PNG, JPG, JPEG, GIF, and WEBP.')
      return
    }
    setError(null)
    onChange(file)
  }

  const handleRemove = () => {
    setPreviewSrc(null)
    setError(null)
    onChange(null)

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  return (
    <div>
      <Label htmlFor="affiliation-logo">Logo (Optional)</Label>
      <input
        id="affiliation-logo"
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/png,image/jpeg,image/gif,image/webp"
        disabled={disabled}
        onChange={(event) => {
          handleFileSelected(event.target.files?.[0])
        }}
      />

      {!currentPreview && (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={openFilePicker}
            >
              Upload Logo
            </Button>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Supported formats: PNG, JPG, JPEG, GIF, WEBP.
          </p>
        </div>
      )}

      {currentPreview && (
        <div className="space-y-3 rounded-md border border-gray-200 p-4">
          <div className="flex min-h-24 items-center justify-center rounded-md bg-gray-50 p-4">
            <Image
              path={typeof value === 'string' ? value : undefined}
              src={previewSrc ?? undefined}
              alt="Affiliation logo preview"
              className="max-h-20 w-auto object-contain"
            />
          </div>

          <div className="flex justify-center gap-2">
            <Button
              type="button"
              variant="destructive"
              disabled={disabled}
              onClick={handleRemove}
            >
              Remove Logo
            </Button>

            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={openFilePicker}
            >
              Replace Logo
            </Button>
          </div>
        </div>
      )}

      <FormErrorMessage message={error ?? undefined} />
    </div>
  )
}
